import { useState, useEffect, useRef, useCallback } from "react";
import { getParkingConfig, saveParkingConfig, deleteParkingConfig, getParkingBackups, saveParkingBackup, restoreParkingBackup, deleteParkingBackup, exportParkingBackup, importParkingBackup, getSettings, uploadApronMap, deleteApronMap, updateApronMapTransform } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { 
  Plane, Plus, Trash2, Save, RotateCw, Move, 
  ZoomIn, ZoomOut, Maximize2, Loader2, Check, GripVertical, Settings2,
  Ban, Calendar as CalendarIcon, Download, Upload, History, Archive, Map, Image as ImageIcon, Move3D, MapPin, Eye
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import apronChartDefault from "@/assets/apron_chart.jpg";

// Default apron chart image (bundled locally so it always loads in every environment)
const DEFAULT_APRON_IMAGE_URL = apronChartDefault;

// Aircraft shape definitions - SVG paths for different aircraft types
const AIRCRAFT_SHAPES = {
  // P1 Apron - Turboprop with T-tail (stands 1-4 A/B) - pointed nose, straight wings, T-tail
  "P1": {
    name: "P1 - Turboprop",
    // Pointed nose, straight swept wings, T-tail with horizontal stabilizers
    path: "M20 1 L23 12 L38 18 L38 22 L23 20 L23 30 L18 30 L18 34 L14 34 L14 38 L20 36 L26 38 L26 34 L22 34 L22 30 L17 30 L17 20 L2 22 L2 18 L17 12 Z",
    viewBox: "0 0 40 40"
  },
  // P2 Apron - Light jet with T-tail (similar to P1 but smaller wings)
  "P2": {
    name: "P2 - Light Jet", 
    // Similar to P1 but with jet-style proportions
    path: "M20 2 L23 14 L36 18 L36 22 L23 20 L23 28 L19 28 L19 32 L15 32 L15 36 L20 34 L25 36 L25 32 L21 32 L21 28 L17 28 L17 20 L4 22 L4 18 L17 14 Z",
    viewBox: "0 0 40 40"
  },
  // P3 Apron - Business jet with rear engines and T-tail (stands 17-18 B/C)
  // Swept wings, rear-mounted engine pods, high T-tail
  "P3": {
    name: "P3 - Business Jet",
    path: "M20 1 L22 14 L38 22 L37 26 L22 22 L22 26 L18 26 L18 28 L15 28 L15 32 L18 32 L18 36 L20 34 L22 36 L22 32 L25 32 L25 28 L22 28 L22 26 L18 22 L3 26 L2 22 L18 14 Z",
    viewBox: "0 0 40 40"
  },
  // P5 Apron - Airliner with underwing engines (stands 30-39)
  // Swept wings, underwing engine pods, conventional tail
  "P5": {
    name: "P5 - Airliner",
    path: "M20 1 L21 4 C22 4 23 5 23 6 L23 14 L38 22 L36 26 L23 20 L23 30 L17 34 L17 38 L20 36 L23 38 L23 34 L17 30 L17 20 L4 26 L2 22 L17 14 L17 6 C17 5 18 4 19 4 Z M12 18 L12 24 L8 24 L7 22 L8 20 Z M28 18 L28 24 L32 24 L33 22 L32 20 Z",
    viewBox: "0 0 40 40"
  },
  // Default - Generic jet shape
  "DEFAULT": {
    name: "Default",
    path: "M20 2 L22 14 L36 18 L36 22 L22 20 L22 32 L16 36 L16 38 L20 36 L24 38 L24 36 L18 32 L18 20 L4 22 L4 18 L18 14 Z",
    viewBox: "0 0 40 40"
  },
  // Helicopter
  "HELI": {
    name: "Helicopter",
    // Body with main rotor blades and tail boom
    path: "M20 12 C25 12 28 15 28 19 C28 23 25 26 20 26 C15 26 12 23 12 19 C12 15 15 12 20 12 Z M3 17 L37 21 M7 13 L33 25 M20 26 L20 30 L30 30 L32 28 L34 30 L32 32 L20 32 Z",
    viewBox: "0 0 40 40"
  }
};

// Function to get default shape based on stand ID
const getDefaultShapeForStand = (standId) => {
  if (!standId) return "DEFAULT";
  const id = standId.toUpperCase();
  // P1: stands 1A, 1B, 2A, 2B, 3A, 3B, 4A, 4B
  if (/^[1-4][AB]$/.test(id)) return "P1";
  // P2: stand P2
  if (id === "P2") return "P2";
  // P3: stands 17B, 17C, 18B, 18C
  if (/^1[78][BC]$/.test(id)) return "P3";
  // P4: stands 19-28 A/B
  if (/^(19|2[0-8])[AB]$/.test(id)) return "P4";
  // P5: stands 30-39
  if (/^3[0-9]$/.test(id)) return "P5";
  // Default to P2 for unknown stands
  return "P2";
};

// Function to get default restrictions based on stand ID
const getDefaultRestrictions = (standId) => {
  if (!standId) return {};
  const id = standId.toUpperCase();
  
  // P1 stands (1-4 A/B): Code A/B aircraft - height 4.78M MAX, length 18M MAX, wingspan 16.61M MAX
  if (/^[1-4][AB]$/.test(id)) {
    return { max_wingspan: 16.61, max_length: 18, max_height: 4.78, tail_wheel_prohibited: false };
  }
  
  // P3 stands (17-18 B/C): wingspan and length 20M MAX
  if (/^1[78][BC]$/.test(id)) {
    return { max_wingspan: 20, max_length: 20, max_height: "", tail_wheel_prohibited: false };
  }
  
  // P4 stands - various restrictions
  if (/^19[AB]$/.test(id) || /^20[AB]$/.test(id) || /^21[AB]$/.test(id)) {
    return { max_wingspan: 13.60, max_length: "", max_height: "", tail_wheel_prohibited: false };
  }
  if (/^22[AB]$/.test(id) || /^25[AB]$/.test(id)) {
    return { max_wingspan: 12, max_length: "", max_height: "", tail_wheel_prohibited: false };
  }
  if (/^23[AB]$/.test(id) || /^24[AB]$/.test(id)) {
    return { max_wingspan: 13.60, max_length: "", max_height: "", tail_wheel_prohibited: false };
  }
  if (/^2[678][AB]$/.test(id)) {
    return { max_wingspan: 13.60, max_length: "", max_height: "", tail_wheel_prohibited: true };
  }
  
  // P5 stands (30-39): tail wheel prohibited
  if (/^3[0-9]$/.test(id)) {
    if (id === "30") {
      return { max_wingspan: 21, max_length: 22, max_height: "", tail_wheel_prohibited: true };
    }
    if (/^3[1-4]$/.test(id)) {
      return { max_wingspan: 32, max_length: "", max_height: "", tail_wheel_prohibited: true };
    }
    return { max_wingspan: "", max_length: "", max_height: "", tail_wheel_prohibited: true };
  }
  
  return {};
};

// Default stands list for quick adding
const DEFAULT_STANDS = [
  // P1
  "1A", "1B", "2A", "2B", "3A", "3B", "4A", "4B",
  // P3
  "17B", "17C", "18B", "18C",
  // P4
  "19A", "19B", "20A", "20B", "21A", "21B", "22A", "22B",
  "23A", "23B", "24A", "24B", "25A", "25B", "26A", "26B",
  "27A", "27B", "28A", "28B",
  // P5
  "30", "31", "32", "33", "34", "35", "36", "37", "38", "39"
];

export default function ParkingConfigPage() {
  const { settings, fetchSettings } = useAuth();
  const { isMasterAdmin, isAdmin, isExposition } = usePermissions();
  // In Exposition (Demo) mode, controls stay visible but clicking them shows the
  // global "Demo Mode — Visual Only" popup instead of performing the action.
  const demoBlock = () => window.dispatchEvent(new CustomEvent("demo-mode-block"));
  const guard = (fn) => (isExposition ? demoBlock : fn);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stands, setStands] = useState([]);
  const [selectedStand, setSelectedStand] = useState(null);
  const [zoom, setZoom] = useState(0.50);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMovingStand, setIsMovingStand] = useState(false);
  const [newStandId, setNewStandId] = useState("");
  const [newStandApron, setNewStandApron] = useState("");
  const [newStandShowVisual, setNewStandShowVisual] = useState(false);
  const [showApronPicker, setShowApronPicker] = useState(false);
  const [pendingStandId, setPendingStandId] = useState("");
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  
  // Backup state
  const [showBackupDialog, setShowBackupDialog] = useState(false);
  const [backups, setBackups] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupName, setBackupName] = useState("");
  
  // Map upload state
  const [showMapUploadDialog, setShowMapUploadDialog] = useState(false);
  const [uploadingMap, setUploadingMap] = useState(false);
  const mapFileInputRef = useRef(null);
  
  // Map transform state (scale and offset)
  const [mapTransform, setMapTransform] = useState({
    scale: settings?.apron_map_scale || 1.0,
    offsetX: settings?.apron_map_offset_x || 0,
    offsetY: settings?.apron_map_offset_y || 0
  });
  const [savingTransform, setSavingTransform] = useState(false);
  const [isAdjustingMap, setIsAdjustingMap] = useState(false);
  
  // Get the apron map URL - use custom map from settings if available
  const apronMapUrl = settings?.apron_map_base64 || DEFAULT_APRON_IMAGE_URL;
  
  // Update map transform when settings load
  useEffect(() => {
    if (settings) {
      setMapTransform({
        scale: settings.apron_map_scale || 1.0,
        offsetX: settings.apron_map_offset_x || 0,
        offsetY: settings.apron_map_offset_y || 0
      });
    }
  }, [settings]);
  
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load existing configurations
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getParkingConfig();
      setStands(res.data.stands || []);
    } catch (error) {
      console.error("Error loading parking config:", error);
      toast.error("Failed to load parking configurations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Get image dimensions when loaded
  const handleImageLoad = (e) => {
    setImageSize({
      width: e.target.naturalWidth,
      height: e.target.naturalHeight
    });
  };

  // Add new stand - step 1: show apron picker
  const initiateAddStand = (standId) => {
    if (!standId.trim()) return;
    const id = standId.trim().toUpperCase();
    if (stands.find(s => s.stand_id === id)) {
      toast.error("Stand already exists");
      return;
    }
    setPendingStandId(id);
    // Pre-select apron based on pattern
    const guessApron = /^[1-4][AB]$/i.test(id) ? "P1" :
                       /^P2/i.test(id) ? "P2" :
                       /^1[78][BC]$/i.test(id) ? "P3" :
                       /^(19|2[0-8])[AB]?$/i.test(id) ? "P4" :
                       /^3[0-9]$/i.test(id) ? "P5" :
                       /^(S\d|CICADE)/i.test(id) ? "Hangars" : "";
    setNewStandApron(guessApron);
    setShowApronPicker(true);
  };

  // Add new stand - step 2: confirm with selected apron
  const addStand = (standId, apron) => {
    if (!standId.trim() || !apron) return;
    
    const id = standId.trim().toUpperCase();
    
    // Place new stand in center of visible area
    const container = containerRef.current;
    const centerX = container ? (container.clientWidth / 2 - pan.x) / zoom : 400;
    const centerY = container ? (container.clientHeight / 2 - pan.y) / zoom : 400;
    
    // Set default restrictions based on stand ID
    const defaultRestrictions = getDefaultRestrictions(id);
    
    const newStand = {
      stand_id: id,
      x: Math.round(centerX),
      y: Math.round(centerY),
      rotation: 0,
      scale: 1.0,
      apron: apron,
      shape: getDefaultShapeForStand(id),
      deactivated: false,
      deactivation_start: "",
      deactivation_end: "",
      deactivation_reason: "",
      max_wingspan: defaultRestrictions.max_wingspan || "",
      max_length: defaultRestrictions.max_length || "",
      max_height: defaultRestrictions.max_height || "",
      tail_wheel_prohibited: defaultRestrictions.tail_wheel_prohibited || false,
      show_visual: newStandShowVisual
    };
    
    setStands([...stands, newStand]);
    setSelectedStand(newStand);
    setNewStandId("");
    setPendingStandId("");
    setShowApronPicker(false);
    setNewStandShowVisual(false);
    toast.success(`Stand ${id} added to ${apron} - drag it to position`);
  };

  // Delete stand
  const deleteStand = async (standId) => {
    try {
      await deleteParkingConfig(standId);
      setStands(stands.filter(s => s.stand_id !== standId));
      if (selectedStand?.stand_id === standId) {
        setSelectedStand(null);
      }
      toast.success(`Stand ${standId} deleted`);
    } catch (error) {
      console.error("Error deleting stand:", error);
      toast.error("Failed to delete stand");
    }
  };

  // Save stand configuration
  const saveStand = async (stand) => {
    setSaving(true);
    try {
      await saveParkingConfig(stand);
      toast.success(`Stand ${stand.stand_id} position fixed!`);
    } catch (error) {
      console.error("Error saving stand:", error);
      toast.error("Failed to save stand configuration");
    } finally {
      setSaving(false);
    }
  };

  // Update selected stand property
  const updateSelectedStand = (property, value) => {
    if (!selectedStand) return;
    
    const updated = { ...selectedStand, [property]: value };
    setSelectedStand(updated);
    setStands(prev => prev.map(s => s.stand_id === updated.stand_id ? updated : s));
  };

  // Update selected stand position (X and Y together)
  const updateSelectedStandPosition = (newX, newY) => {
    if (!selectedStand) return;
    
    const updated = { ...selectedStand, x: newX, y: newY };
    setSelectedStand(updated);
    setStands(prev => prev.map(s => s.stand_id === updated.stand_id ? updated : s));
  };

  // Handle mouse events for panning
  const handleMouseDown = (e) => {
    // Don't start pan if clicking on a stand
    if (e.target.closest('.stand-marker')) return;
    
    if (!isMovingStand) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && !isMovingStand) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
    
    // Handle stand dragging - update X and Y together
    // Stand positions are independent of map transform
    if (isMovingStand && selectedStand) {
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const newX = (e.clientX - rect.left - pan.x) / zoom;
      const newY = (e.clientY - rect.top - pan.y) / zoom;
      
      // Allow free movement - no clamping
      const clampedX = Math.round(Math.max(-500, newX));
      const clampedY = Math.round(Math.max(-500, newY));
      
      // Update both X and Y in single state update
      updateSelectedStandPosition(clampedX, clampedY);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (isMovingStand) {
      setIsMovingStand(false);
    }
  };

  // Handle wheel zoom - extended range for new map
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prev => Math.min(Math.max(prev + delta, 0.3), 5));
  };

  // Reset view - fit to window
  const resetView = () => {
    setZoom(0.50);
    setPan({ x: 0, y: 0 });
  };

  // Fit to screen
  const fitToScreen = () => {
    const container = containerRef.current;
    if (!container || !imageSize.width) return;
    
    const scaleX = container.clientWidth / imageSize.width;
    const scaleY = container.clientHeight / imageSize.height;
    const newZoom = Math.min(scaleX, scaleY) * 0.95;
    
    setZoom(newZoom);
    setPan({ x: 0, y: 0 });
  };

  // Get scaled position - aircraft/stand positions are independent of map transform
  const getScaledPosition = (stand) => {
    return {
      x: stand.x * zoom + pan.x,
      y: stand.y * zoom + pan.y
    };
  };

  // Save all stands
  const saveAllStands = async () => {
    setSaving(true);
    try {
      for (const stand of stands) {
        await saveParkingConfig(stand);
      }
      toast.success("All parking positions saved!");
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Failed to save configurations");
    } finally {
      setSaving(false);
    }
  };

  // Backup functions
  const loadBackups = async () => {
    setBackupLoading(true);
    try {
      const res = await getParkingBackups();
      setBackups(res.data.backups || []);
    } catch (error) {
      console.error("Error loading backups:", error);
      toast.error("Failed to load backups");
    } finally {
      setBackupLoading(false);
    }
  };

  const createBackup = async () => {
    if (!backupName.trim()) {
      toast.error("Please enter a backup name");
      return;
    }
    setBackupLoading(true);
    try {
      await saveParkingBackup(backupName.trim());
      toast.success("Backup created successfully");
      setBackupName("");
      loadBackups();
    } catch (error) {
      console.error("Error creating backup:", error);
      toast.error("Failed to create backup");
    } finally {
      setBackupLoading(false);
    }
  };

  const restoreFromBackup = async (backupId) => {
    if (!window.confirm("Are you sure you want to restore this backup? Current configuration will be replaced.")) {
      return;
    }
    setBackupLoading(true);
    try {
      await restoreParkingBackup(backupId);
      toast.success("Backup restored successfully");
      loadConfig();
      setShowBackupDialog(false);
    } catch (error) {
      console.error("Error restoring backup:", error);
      toast.error("Failed to restore backup");
    } finally {
      setBackupLoading(false);
    }
  };

  const deleteBackup = async (backupId) => {
    if (!window.confirm("Are you sure you want to delete this backup?")) {
      return;
    }
    try {
      await deleteParkingBackup(backupId);
      toast.success("Backup deleted");
      loadBackups();
    } catch (error) {
      console.error("Error deleting backup:", error);
      toast.error("Failed to delete backup");
    }
  };

  const handleExportBackup = async () => {
    try {
      const res = await exportParkingBackup();
      const data = res.data;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `parking-config-backup-${format(new Date(), "yyyy-MM-dd-HHmm")}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Configuration exported");
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error("Failed to export configuration");
    }
  };

  const handleImportBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!data.configurations || !Array.isArray(data.configurations)) {
        toast.error("Invalid backup file format");
        return;
      }
      
      await importParkingBackup(data);
      toast.success(`Imported ${data.configurations.length} configurations`);
      loadConfig();
    } catch (error) {
      console.error("Error importing:", error);
      toast.error("Failed to import configuration");
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const openBackupDialog = () => {
    setShowBackupDialog(true);
    loadBackups();
  };

  // Handle map upload
  const handleMapUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image file is too large (max 10MB)");
      return;
    }
    
    setUploadingMap(true);
    try {
      await uploadApronMap(file);
      toast.success("Apron map uploaded successfully");
      // Refresh settings to get the new map
      if (fetchSettings) {
        await fetchSettings();
      }
      setShowMapUploadDialog(false);
    } catch (error) {
      console.error("Error uploading map:", error);
      toast.error("Failed to upload apron map");
    } finally {
      setUploadingMap(false);
      if (mapFileInputRef.current) {
        mapFileInputRef.current.value = "";
      }
    }
  };

  // Handle map delete (revert to default)
  const handleDeleteMap = async () => {
    setUploadingMap(true);
    try {
      await deleteApronMap();
      toast.success("Custom map removed, reverted to default");
      if (fetchSettings) {
        await fetchSettings();
      }
      setShowMapUploadDialog(false);
    } catch (error) {
      console.error("Error deleting map:", error);
      toast.error("Failed to remove custom map");
    } finally {
      setUploadingMap(false);
    }
  };

  // Save map transform (scale and offset)
  const handleSaveMapTransform = async () => {
    setSavingTransform(true);
    try {
      await updateApronMapTransform({
        scale: mapTransform.scale,
        offset_x: mapTransform.offsetX,
        offset_y: mapTransform.offsetY
      });
      toast.success("Map position saved successfully");
      if (fetchSettings) {
        await fetchSettings();
      }
      setIsAdjustingMap(false);
    } catch (error) {
      console.error("Error saving map transform:", error);
      toast.error("Failed to save map position");
    } finally {
      setSavingTransform(false);
    }
  };

  // Reset map transform to defaults
  const handleResetMapTransform = () => {
    setMapTransform({
      scale: 1.0,
      offsetX: 0,
      offsetY: 0
    });
  };

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="parking-config-page">
      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={handleImportBackup}
      />
      
      {/* Hidden file input for map upload */}
      <input
        ref={mapFileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleMapUpload}
      />
      
      {/* Sticky Header */}
      <div className="sticky-page-header flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0f172a] dark:text-white flex items-center gap-2">
            <Settings2 size={24} className="text-[#d4af37]" />
            Parking Position Configuration
          </h1>
          <p className="hidden md:block text-[#64748b] dark:text-gray-400">
            Configure exact positions and orientations for each parking stand. Click to select, drag to position.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Map Upload - Admin / Demo (clicks show demo popup for Exposition) */}
          {(isMasterAdmin || isAdmin || isExposition) && (
            <>
              <Button onClick={guard(() => setShowMapUploadDialog(true))} variant="outline" className="gap-2" data-testid="map-upload-btn">
                <Map size={16} />
                Change Map
              </Button>
              <Button 
                onClick={guard(() => setIsAdjustingMap(!isAdjustingMap))} 
                variant={isAdjustingMap ? "default" : "outline"} 
                className={`gap-2 ${isAdjustingMap ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                data-testid="adjust-map-btn"
              >
                <Move3D size={16} />
                {isAdjustingMap ? "Exit Adjust Mode" : "Adjust Map"}
              </Button>
            </>
          )}
          {/* Backup / mutation options (clicks show demo popup for Exposition) */}
          <Button onClick={guard(openBackupDialog)} variant="outline" className="gap-2" data-testid="backups-btn">
            <Archive size={16} />
            Backups
          </Button>
          <Button onClick={guard(handleExportBackup)} variant="outline" className="gap-2" data-testid="export-btn">
            <Download size={16} />
            Export
          </Button>
          <Button onClick={guard(() => fileInputRef.current?.click())} variant="outline" className="gap-2" data-testid="import-btn">
            <Upload size={16} />
            Import
          </Button>
          <Button onClick={guard(saveAllStands)} disabled={saving} className="bg-[#d4af37] hover:bg-[#b8972e]" data-testid="save-all-positions-btn">
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
            Save All Positions
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row gap-4 min-h-0">
        {/* Chart Canvas - Full Width */}
        <div className="flex-1 flex flex-col min-w-0 min-h-[55vh] md:min-h-0">
          {/* Toolbar */}
          <div className="flex items-center gap-2 mb-2 bg-white dark:bg-[#1e293b] rounded-lg p-2 shadow">
            <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.max(z - 0.1, 0.3))}>
              <ZoomOut size={18} />
            </Button>
            <span className="text-sm text-[#64748b] w-16 text-center font-medium">{Math.round(zoom * 100)}%</span>
            <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.min(z + 0.1, 5))}>
              <ZoomIn size={18} />
            </Button>
            <div className="w-px h-6 bg-[#e2e8f0] dark:bg-[#334155]" />
            <Button variant="ghost" size="sm" onClick={resetView} title="Reset View">
              <Maximize2 size={18} />
            </Button>
            <Button variant="ghost" size="sm" onClick={fitToScreen} title="Fit to Screen">
              Fit
            </Button>
            <div className="flex-1" />
            <span className="text-xs text-[#64748b]">
              {selectedStand ? `Selected: ${selectedStand.stand_id}` : "Click a stand or add new one"}
            </span>
          </div>
          
          {/* Map Adjustment Controls - shown when in adjust mode */}
          {isAdjustingMap && (isMasterAdmin || isAdmin || isExposition) && (
            <div className="mb-2 bg-blue-900/50 border border-blue-500 rounded-lg p-3 shadow">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Label className="text-white text-sm whitespace-nowrap">Map Scale:</Label>
                  <Slider
                    value={[mapTransform.scale]}
                    onValueChange={([v]) => setMapTransform(prev => ({ ...prev, scale: v }))}
                    min={0.5}
                    max={5}
                    step={0.1}
                    className="w-32"
                  />
                  <span className="text-white text-sm w-12">{mapTransform.scale.toFixed(1)}x</span>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-white text-sm whitespace-nowrap">X Offset:</Label>
                  <Input
                    type="number"
                    value={mapTransform.offsetX}
                    onChange={(e) => setMapTransform(prev => ({ ...prev, offsetX: parseInt(e.target.value) || 0 }))}
                    className="w-20 h-8 bg-blue-800 border-blue-600 text-white"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-white text-sm whitespace-nowrap">Y Offset:</Label>
                  <Input
                    type="number"
                    value={mapTransform.offsetY}
                    onChange={(e) => setMapTransform(prev => ({ ...prev, offsetY: parseInt(e.target.value) || 0 }))}
                    className="w-20 h-8 bg-blue-800 border-blue-600 text-white"
                  />
                </div>
                <div className="flex-1" />
                <Button variant="outline" size="sm" onClick={handleResetMapTransform} className="text-white border-white hover:bg-white/20">
                  Reset
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleSaveMapTransform} 
                  disabled={savingTransform}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {savingTransform ? <Loader2 className="animate-spin mr-1" size={14} /> : <Save size={14} className="mr-1" />}
                  Save Position
                </Button>
              </div>
              <p className="text-blue-200 text-xs mt-2">
                Adjust map scale and position. This affects how the map aligns with parking stand markers. Aircraft positions are independent.
              </p>
            </div>
          )}
          
          {/* Map Container */}
          <div 
            ref={containerRef}
            className="flex-1 relative overflow-hidden bg-[#1e293b] rounded-lg cursor-grab active:cursor-grabbing border-2 border-[#334155]"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
          >
            {/* Apron Image - with map transform applied */}
            <img
              ref={imageRef}
              src={apronMapUrl}
              alt="EBCI Apron Chart"
              className="absolute pointer-events-none select-none"
              style={{
                transform: `translate(${pan.x + (mapTransform.offsetX * zoom)}px, ${pan.y + (mapTransform.offsetY * zoom)}px) scale(${zoom * mapTransform.scale})`,
                transformOrigin: "top left",
                maxWidth: "none"
              }}
              draggable={false}
              onLoad={handleImageLoad}
            />

            {/* Stand Markers */}
            {stands.map((stand) => {
              const pos = getScaledPosition(stand);
              const isSelected = selectedStand?.stand_id === stand.stand_id;
              const scale = (stand.scale || 1) * zoom;
              const isDeactivated = stand.deactivated;
              
              // Check if stand is currently deactivated based on date range
              const today = new Date().toISOString().split('T')[0];
              const isCurrentlyDeactivated = isDeactivated && 
                (!stand.deactivation_start || stand.deactivation_start <= today) &&
                (!stand.deactivation_end || stand.deactivation_end >= today);
              
              // Get the shape definition
              const shapeKey = stand.shape || "DEFAULT";
              const shape = AIRCRAFT_SHAPES[shapeKey] || AIRCRAFT_SHAPES["DEFAULT"];
              
              // Determine colors based on state
              const fillColor = isSelected ? "#d4af37" : (isCurrentlyDeactivated ? "#6b7280" : "#dc2626");
              const labelBgColor = isSelected ? "bg-[#d4af37]" : (isCurrentlyDeactivated ? "bg-gray-500" : "bg-[#dc2626]");
              
              return (
                <div
                  key={stand.stand_id}
                  className={`stand-marker absolute cursor-pointer transition-shadow ${
                    isSelected ? "z-30" : "z-20"
                  } ${isCurrentlyDeactivated ? "opacity-60" : ""}`}
                  style={{
                    left: pos.x - 20 * scale,
                    top: pos.y - 20 * scale,
                    transform: `rotate(${stand.rotation}deg)`,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedStand(stand);
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setSelectedStand(stand);
                    setIsMovingStand(true);
                  }}
                >
                  <svg 
                    width={40 * scale} 
                    height={40 * scale} 
                    viewBox={shape.viewBox}
                    className={`drop-shadow-lg ${isSelected ? "filter brightness-110" : ""}`}
                  >
                    {/* Selection ring */}
                    {isSelected && (
                      <circle cx="20" cy="20" r="19" fill="none" stroke="#d4af37" strokeWidth="2" strokeDasharray="4 2" />
                    )}
                    {/* Deactivated X marker */}
                    {isCurrentlyDeactivated && !isSelected && (
                      <>
                        <line x1="8" y1="8" x2="32" y2="32" stroke="#ef4444" strokeWidth="3" />
                        <line x1="32" y1="8" x2="8" y2="32" stroke="#ef4444" strokeWidth="3" />
                      </>
                    )}
                    {/* Aircraft silhouette */}
                    <path
                      d={shape.path}
                      fill={fillColor}
                      stroke="#fff"
                      strokeWidth="1.5"
                    />
                  </svg>
                  {/* Registration label */}
                  <div 
                    className={`absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-bold px-1.5 py-0.5 rounded ${labelBgColor} text-white`}
                    style={{ 
                      transform: `rotate(${-stand.rotation}deg) translateX(-50%)`,
                      top: `${45 * scale}px`,
                      fontSize: `${Math.max(10, 12 * scale)}px`
                    }}
                  >
                    {stand.stand_id}
                    {isCurrentlyDeactivated && " ⛔"}
                  </div>
                </div>
              );
            })}

            {/* Instructions overlay */}
            {stands.length === 0 && !loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <div className="bg-white dark:bg-[#1e293b] p-6 rounded-lg shadow-xl text-center max-w-md">
                  <Move size={48} className="mx-auto mb-4 text-[#d4af37]" />
                  <h3 className="text-lg font-bold mb-2">No Parking Stands Configured</h3>
                  <p className="text-sm text-[#64748b] mb-4">
                    Use the panel on the right to add parking stands, then drag them to their exact positions on the chart.
                  </p>
                </div>
              </div>
            )}

            {/* Loading overlay */}
            {loading && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
                <Loader2 className="w-10 h-10 animate-spin text-[#d4af37]" />
              </div>
            )}
          </div>
          
          <p className="text-xs text-[#64748b] mt-2">
            💡 Click to select a stand (turns gold), then drag to position. Use scroll wheel to zoom. Pan by dragging the background.
          </p>
        </div>

        {/* Right Panel - Controls */}
        <div className="w-full md:w-80 flex flex-col gap-3 min-h-0 overflow-hidden">
          {/* Add New Stand - Collapsible */}
          <Card className="bg-white dark:bg-[#1e293b] shrink-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Plus size={16} className="text-[#d4af37]" />
                Add Parking Stand
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-40 overflow-y-auto">
              <div className="flex gap-2">
                <Input
                  value={newStandId}
                  onChange={(e) => setNewStandId(e.target.value.toUpperCase())}
                  placeholder="Stand ID (e.g. 5A)"
                  className="flex-1"
                  onKeyDown={(e) => e.key === "Enter" && (isExposition ? demoBlock() : initiateAddStand(newStandId))}
                />
                <Button onClick={guard(() => initiateAddStand(newStandId))} size="icon" className="bg-[#d4af37] hover:bg-[#b8972e]">
                  <Plus size={16} />
                </Button>
              </div>
              
              {/* Quick add - single row per apron */}
              <div className="space-y-1">
                {[
                  { label: "P1", stands: ["1A", "1B", "2A", "2B", "3A", "3B", "4A", "4B"] },
                  { label: "P2", stands: ["P2"] },
                  { label: "P3", stands: ["17B", "17C", "18B", "18C"] },
                  { label: "P4", stands: ["19A", "19B", "20A", "20B", "21A", "21B", "22A", "22B", "23A", "23B", "24A", "24B", "25A", "25B", "26A", "26B", "27A", "27B", "28A", "28B"] },
                  { label: "P5", stands: ["30", "31", "32", "33", "34", "35", "36", "37", "38", "39"] },
                  { label: "HG", stands: ["S10", "CICADE", "S15", "S16", "S17"] },
                ].map(group => (
                  <div key={group.label} className="flex items-center gap-1 flex-wrap">
                    <span className="text-xs font-bold text-[#d4af37] w-6">{group.label}:</span>
                    {group.stands.filter(s => !stands.find(st => st.stand_id === s)).slice(0, 8).map(standId => (
                      <Button
                        key={standId}
                        variant="outline"
                        size="sm"
                        className="text-xs h-5 px-1.5"
                        onClick={guard(() => initiateAddStand(standId))}
                      >
                        {standId}
                      </Button>
                    ))}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Selected Stand Controls - Main Panel */}
          {selectedStand ? (
            <Card className="bg-white dark:bg-[#1e293b] border-2 border-[#d4af37] flex-1 min-h-0 overflow-hidden flex flex-col">
              <CardHeader className="pb-1 shrink-0">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Plane size={16} className="text-[#d4af37]" />
                    Stand {selectedStand.stand_id}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={guard(() => deleteStand(selectedStand.stand_id))}
                  >
                    <Trash2 size={14} />
                  </Button>                </CardTitle>
              </CardHeader>
              <ScrollArea className="flex-1">
                <div className="p-4 pt-0 space-y-3" onMouseDown={(e) => e.stopPropagation()}>
                {/* Position */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">X Position</Label>
                    <Input
                      type="number"
                      value={selectedStand.x}
                      onChange={(e) => updateSelectedStand("x", parseInt(e.target.value) || 0)}
                      className="h-8"
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Y Position</Label>
                    <Input
                      type="number"
                      value={selectedStand.y}
                      onChange={(e) => updateSelectedStand("y", parseInt(e.target.value) || 0)}
                      className="h-8"
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>

                {/* Rotation */}
                <div onMouseDown={(e) => e.stopPropagation()}>
                  <Label className="text-xs flex items-center justify-between">
                    <span>Rotation: {selectedStand.rotation}°</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 px-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        updateSelectedStand("rotation", (selectedStand.rotation + 45) % 360);
                      }}
                    >
                      <RotateCw size={12} className="mr-1" /> +45°
                    </Button>
                  </Label>
                  <Slider
                    value={[selectedStand.rotation || 0]}
                    onValueChange={(v) => updateSelectedStand("rotation", v[0])}
                    min={0}
                    max={359}
                    step={5}
                    className="mt-2"
                  />
                  <div className="flex gap-1 mt-2">
                    {[0, 90, 180, 270].map(angle => (
                      <Button
                        key={angle}
                        variant={selectedStand.rotation === angle ? "default" : "outline"}
                        size="sm"
                        className={`flex-1 h-7 text-xs ${selectedStand.rotation === angle ? "bg-[#d4af37] hover:bg-[#b8972e]" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateSelectedStand("rotation", angle);
                        }}
                      >
                        {angle}°
                      </Button>
                    ))}
                  </div>
                  <div className="text-xs text-[#64748b] mt-1">
                    0° = nose up | 90° = nose right | 180° = nose down | 270° = nose left
                  </div>
                </div>

                {/* Scale */}
                <div onMouseDown={(e) => e.stopPropagation()}>
                  <Label className="text-xs">Size: {(selectedStand.scale || 1).toFixed(1)}x</Label>
                  <Slider
                    value={[(selectedStand.scale || 1) * 10]}
                    onValueChange={(v) => updateSelectedStand("scale", v[0] / 10)}
                    min={5}
                    max={100}
                    step={1}
                    className="mt-2"
                  />
                </div>

                {/* Aircraft Shape */}
                <div onMouseDown={(e) => e.stopPropagation()}>
                  <Label className="text-xs mb-2 block">Aircraft Shape: {AIRCRAFT_SHAPES[selectedStand.shape || "DEFAULT"]?.name}</Label>
                  <div className="grid grid-cols-4 gap-1">
                    {Object.entries(AIRCRAFT_SHAPES).map(([key, shape]) => (
                      <Button
                        key={key}
                        variant={(selectedStand.shape || "DEFAULT") === key ? "default" : "outline"}
                        size="sm"
                        className={`h-12 p-1 flex flex-col items-center justify-center ${
                          (selectedStand.shape || "DEFAULT") === key ? "bg-[#d4af37] hover:bg-[#b8972e] ring-2 ring-[#d4af37]" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateSelectedStand("shape", key);
                        }}
                        title={shape.name}
                      >
                        <svg width="24" height="24" viewBox={shape.viewBox} className="shrink-0">
                          <path 
                            d={shape.path} 
                            fill={(selectedStand.shape || "DEFAULT") === key ? "#fff" : "#dc2626"} 
                            stroke={(selectedStand.shape || "DEFAULT") === key ? "#d4af37" : "#fff"} 
                            strokeWidth="1" 
                          />
                        </svg>
                        <span className="text-[8px] mt-0.5 leading-none">{key}</span>
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Apron */}
                <div onMouseDown={(e) => e.stopPropagation()}>
                  <Label className="text-xs">Apron</Label>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {["P1", "P2", "P3", "P4", "P5", "Hangars"].map(apron => (
                      <Button
                        key={apron}
                        variant={selectedStand.apron === apron ? "default" : "outline"}
                        size="sm"
                        className={`h-7 text-xs ${selectedStand.apron === apron ? "bg-[#d4af37] hover:bg-[#b8972e]" : ""}`}
                        onClick={() => updateSelectedStand("apron", apron)}
                      >
                        {apron}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Stand Restrictions */}
                <div className="border-t border-[#e2e8f0] dark:border-[#334155] pt-3 mt-1" onMouseDown={(e) => e.stopPropagation()}>
                  <Label className="text-xs font-semibold mb-2 block text-orange-600 dark:text-orange-400">
                    Stand Restrictions
                  </Label>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <div>
                      <Label className="text-[10px] text-[#64748b]">Max Wingspan (m)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={selectedStand.max_wingspan || ""}
                        onChange={(e) => updateSelectedStand("max_wingspan", e.target.value ? parseFloat(e.target.value) : "")}
                        placeholder="e.g. 16.61"
                        className="h-7 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] text-[#64748b]">Max Length (m)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={selectedStand.max_length || ""}
                        onChange={(e) => updateSelectedStand("max_length", e.target.value ? parseFloat(e.target.value) : "")}
                        placeholder="e.g. 18"
                        className="h-7 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] text-[#64748b]">Max Height (m)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={selectedStand.max_height || ""}
                        onChange={(e) => updateSelectedStand("max_height", e.target.value ? parseFloat(e.target.value) : "")}
                        placeholder="e.g. 4.78"
                        className="h-7 text-xs"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between bg-orange-50 dark:bg-orange-900/20 p-2 rounded">
                    <Label className="text-xs flex items-center gap-1 text-orange-700 dark:text-orange-300">
                      <Ban size={12} />
                      Tail Wheel Prohibited
                    </Label>
                    <Switch
                      checked={selectedStand.tail_wheel_prohibited || false}
                      onCheckedChange={(checked) => updateSelectedStand("tail_wheel_prohibited", checked)}
                    />
                  </div>
                </div>

                {/* Visual Indicator Toggle */}
                <div className="border-t border-[#e2e8f0] dark:border-[#334155] pt-3 mt-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs flex items-center gap-1">
                      <Eye size={12} className={selectedStand.show_visual ? "text-blue-500" : "text-gray-400"} />
                      Show Visual Circle on Map
                    </Label>
                    <Switch
                      checked={selectedStand.show_visual || false}
                      onCheckedChange={(checked) => updateSelectedStand("show_visual", checked)}
                    />
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Display blue availability ring and label on the apron map</p>
                </div>

                {/* Stand Deactivation */}
                <div className="border-t border-[#e2e8f0] dark:border-[#334155] pt-3 mt-1" onMouseDown={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs flex items-center gap-1">
                      <Ban size={12} className={selectedStand.deactivated ? "text-red-500" : "text-gray-400"} />
                      Stand Deactivation
                    </Label>
                    <Switch
                      checked={selectedStand.deactivated || false}
                      onCheckedChange={(checked) => updateSelectedStand("deactivated", checked)}
                    />
                  </div>
                  
                  {selectedStand.deactivated && (
                    <div className="space-y-2 bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-red-600 dark:text-red-400">From Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full h-8 text-xs justify-start border-red-300 dark:border-red-700"
                              >
                                <CalendarIcon size={12} className="mr-1" />
                                {selectedStand.deactivation_start ? format(new Date(selectedStand.deactivation_start), "dd/MM/yyyy") : "Select"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={selectedStand.deactivation_start ? new Date(selectedStand.deactivation_start) : undefined}
                                onSelect={(date) => updateSelectedStand("deactivation_start", date ? format(date, "yyyy-MM-dd") : "")}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <Label className="text-xs text-red-600 dark:text-red-400">To Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full h-8 text-xs justify-start border-red-300 dark:border-red-700"
                              >
                                <CalendarIcon size={12} className="mr-1" />
                                {selectedStand.deactivation_end ? format(new Date(selectedStand.deactivation_end), "dd/MM/yyyy") : "Select"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={selectedStand.deactivation_end ? new Date(selectedStand.deactivation_end) : undefined}
                                onSelect={(date) => updateSelectedStand("deactivation_end", date ? format(date, "yyyy-MM-dd") : "")}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-red-600 dark:text-red-400">Reason</Label>
                        <Input
                          value={selectedStand.deactivation_reason || ""}
                          onChange={(e) => updateSelectedStand("deactivation_reason", e.target.value)}
                          placeholder="Maintenance, etc."
                          className="h-8 text-xs border-red-300 dark:border-red-700"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Save button */}
                <Button 
                  onClick={guard(() => saveStand(selectedStand))} 
                  disabled={saving}
                  className="w-full bg-[#d4af37] hover:bg-[#b8972e]"
                >
                  {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Check size={16} className="mr-2" />}
                  Fix Position
                </Button>
                </div>
              </ScrollArea>
            </Card>
          ) : (
            <Card className="bg-white dark:bg-[#1e293b] flex-1">
              <CardContent className="h-full flex flex-col items-center justify-center text-center py-8">
                <GripVertical size={40} className="mb-4 text-[#64748b] opacity-30" />
                <p className="text-sm text-[#64748b]">Select a stand on the chart</p>
                <p className="text-xs text-[#64748b] mt-1">or add a new one above</p>
              </CardContent>
            </Card>
          )}

          {/* Configured Stands List - Grouped by Apron */}
          <Card className="bg-white dark:bg-[#1e293b] shrink-0">
            <CardHeader className="pb-1">
              <CardTitle className="text-xs flex items-center justify-between">
                <span>Configured ({stands.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-48 px-3 pb-2">
                <div className="space-y-2">
                  {(() => {
                    const groups = {};
                    stands.forEach(s => {
                      const apron = s.apron || "Other";
                      if (!groups[apron]) groups[apron] = [];
                      groups[apron].push(s);
                    });
                    Object.keys(groups).forEach(k => {
                      groups[k].sort((a, b) => {
                        const numA = parseInt(a.stand_id.replace(/[^0-9]/g, '')) || 0;
                        const numB = parseInt(b.stand_id.replace(/[^0-9]/g, '')) || 0;
                        return numA !== numB ? numA - numB : a.stand_id.localeCompare(b.stand_id);
                      });
                    });
                    const order = ["P1", "P2", "P3", "P4", "P5", "Hangars", "Other"];
                    const sortedKeys = Object.keys(groups).sort((a, b) => {
                      const ia = order.indexOf(a); const ib = order.indexOf(b);
                      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
                    });
                    return sortedKeys.map(apron => {
                      const today = new Date().toISOString().split('T')[0];
                      return (
                        <div key={apron}>
                          <p className="text-[10px] text-[#d4af37] font-semibold mb-1">{apron}</p>
                          <div className="flex flex-wrap gap-1">
                            {groups[apron].map(stand => {
                              const isCurrentlyDeactivated = stand.deactivated && 
                                (!stand.deactivation_start || stand.deactivation_start <= today) &&
                                (!stand.deactivation_end || stand.deactivation_end >= today);
                              return (
                                <Badge
                                  key={stand.stand_id}
                                  variant={selectedStand?.stand_id === stand.stand_id ? "default" : "outline"}
                                  className={`cursor-pointer text-xs ${
                                    selectedStand?.stand_id === stand.stand_id 
                                      ? "bg-[#d4af37] hover:bg-[#b8972e]" 
                                      : isCurrentlyDeactivated 
                                        ? "bg-gray-400 text-white hover:bg-gray-500" 
                                        : "hover:bg-[#f8fafc]"
                                  }`}
                                  onClick={() => setSelectedStand(stand)}
                                >
                                  {stand.stand_id}{isCurrentlyDeactivated ? " ⛔" : ""}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Apron Picker Dialog - shown when adding new stand */}
      <Dialog open={showApronPicker} onOpenChange={setShowApronPicker}>
        <DialogContent className="max-w-sm" data-testid="apron-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin size={20} className="text-[#d4af37]" />
              Assign Stand {pendingStandId} to Apron
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2">
            {["P1", "P2", "P3", "P4", "P5", "Hangars"].map(apron => (
              <Button
                key={apron}
                variant={newStandApron === apron ? "default" : "outline"}
                className={`h-12 text-sm font-bold ${newStandApron === apron ? "bg-[#d4af37] hover:bg-[#b8972e] ring-2 ring-[#d4af37]" : ""}`}
                onClick={() => setNewStandApron(apron)}
                data-testid={`apron-option-${apron}`}
              >
                {apron}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-[#e2e8f0] dark:border-[#334155]">
            <Switch
              id="show-visual-toggle"
              checked={newStandShowVisual}
              onCheckedChange={setNewStandShowVisual}
            />
            <Label htmlFor="show-visual-toggle" className="text-sm flex items-center gap-1.5 cursor-pointer">
              <Eye size={14} className={newStandShowVisual ? "text-blue-500" : "text-gray-400"} />
              Show visual circle on map
            </Label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApronPicker(false)}>Cancel</Button>
            <Button 
              className="bg-[#d4af37] hover:bg-[#b8972e]"
              disabled={!newStandApron}
              onClick={() => addStand(pendingStandId, newStandApron)}
              data-testid="confirm-apron-btn"
            >
              <Check size={16} className="mr-2" />
              Add to {newStandApron || "..."}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Dialog */}
      <Dialog open={showBackupDialog} onOpenChange={setShowBackupDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive size={20} className="text-[#d4af37]" />
              Parking Configuration Backups
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Create New Backup */}
            <div className="flex gap-2">
              <Input
                value={backupName}
                onChange={(e) => setBackupName(e.target.value)}
                placeholder="Backup name (e.g., Pre-maintenance)"
                className="flex-1"
              />
              <Button 
                onClick={createBackup} 
                disabled={backupLoading || !backupName.trim()}
                className="bg-[#d4af37] hover:bg-[#b8972e]"
              >
                {backupLoading ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
              </Button>
            </div>
            
            {/* Existing Backups */}
            <div className="border rounded-lg">
              <div className="p-2 bg-[#f8fafc] dark:bg-[#0f172a] border-b text-xs font-medium text-[#64748b]">
                Saved Backups ({backups.length})
              </div>
              <ScrollArea className="h-48">
                {backups.length === 0 ? (
                  <div className="p-4 text-center text-sm text-[#64748b]">
                    No backups yet. Create one above.
                  </div>
                ) : (
                  <div className="divide-y">
                    {backups.map((backup) => (
                      <div key={backup.id} className="p-3 flex items-center justify-between hover:bg-[#f8fafc] dark:hover:bg-[#1e293b]">
                        <div>
                          <div className="font-medium text-sm">{backup.name}</div>
                          <div className="text-xs text-[#64748b]">
                            {format(new Date(backup.backup_date), "PPp")} · {backup.stand_count} stands
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => restoreFromBackup(backup.id)}
                          >
                            <History size={14} className="mr-1" />
                            Restore
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => deleteBackup(backup.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBackupDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Map Upload Dialog */}
      <Dialog open={showMapUploadDialog} onOpenChange={setShowMapUploadDialog}>
        <DialogContent className="max-w-md" data-testid="map-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Map size={20} className="text-[#d4af37]" />
              Apron Map Management
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Current Map Preview */}
            <div className="border rounded-lg overflow-hidden">
              <div className="p-2 bg-[#f8fafc] dark:bg-[#0f172a] border-b text-xs font-medium text-[#64748b]">
                Current Map
              </div>
              <div className="p-4 bg-[#1e293b] flex items-center justify-center">
                <img 
                  src={apronMapUrl} 
                  alt="Current Apron Map" 
                  className="max-w-full max-h-40 object-contain rounded"
                />
              </div>
              {settings?.apron_map_base64 && (
                <div className="p-2 bg-[#f0fdf4] text-xs text-green-700 flex items-center gap-1">
                  <Check size={12} />
                  Custom map uploaded
                  {settings?.apron_map_updated_at && (
                    <span className="text-[#64748b] ml-1">
                      · {format(new Date(settings.apron_map_updated_at), "PPp")}
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {/* Upload New Map */}
            <div className="space-y-2">
              <Label>Upload New Map</Label>
              <div className="flex gap-2">
                <Button 
                  onClick={() => mapFileInputRef.current?.click()}
                  disabled={uploadingMap}
                  className="flex-1 bg-[#d4af37] hover:bg-[#b8972e]"
                >
                  {uploadingMap ? (
                    <Loader2 className="animate-spin mr-2" size={16} />
                  ) : (
                    <ImageIcon size={16} className="mr-2" />
                  )}
                  Select Image
                </Button>
              </div>
              <p className="text-xs text-[#64748b]">
                Supports JPG, PNG, WebP. Max file size: 10MB
              </p>
            </div>
            
            {/* Revert to Default */}
            {settings?.apron_map_base64 && (
              <div className="pt-2 border-t">
                <Button 
                  variant="outline" 
                  onClick={handleDeleteMap}
                  disabled={uploadingMap}
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 size={16} className="mr-2" />
                  Remove Custom Map (Revert to Default)
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMapUploadDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
